import { Stack } from '@mui/material'
import { isAreaValue } from 'ComponentLibrary/src/common/area'
import { isMonetaryValue } from 'ComponentLibrary/src/common/money'
import { decimalToLocalizedString, isPercentage, tokens } from 'ComponentLibrary'
import { Typography } from 'ComponentLibrary/src/piazza'
import { DeviationValueType } from './types'
import { isYearPeriod } from 'ComponentLibrary/src/common/year-period'

export const getDeviationValueAndUnit = (
    deviationValue?: DeviationValueType | null,
    usePercentagePoints?: boolean,
    percentagePointUnit = 'BP',
    locale = 'de-DE',
    yearUnitLabels: { one: string; other: string } = { one: 'Jahr', other: 'Jahre' }
) => {
    if (isMonetaryValue(deviationValue)) {
        const value = deviationValue.toDecimal()
        return {
            formattedValue: decimalToLocalizedString(value, false, 2, 2, locale),
            value,
            unit: deviationValue.currencySymbol(),
        }
    }
    if (isAreaValue(deviationValue)) {
        return {
            formattedValue: deviationValue.format({ withUnit: false, minimumNumberOfDigits: 2, locale }),
            value: deviationValue.value,
            unit: deviationValue.unitSymbol(locale),
        }
    }
    if (isPercentage(deviationValue)) {
        const value = usePercentagePoints
            ? percentagePointUnit === 'BP'
                ? deviationValue.toPercentage() * 100
                : deviationValue.toPercentage()
            : deviationValue.toPercentage()
        return {
            formattedValue: decimalToLocalizedString(value, false, 2, 2, locale),
            value,
            unit: usePercentagePoints ? percentagePointUnit : '%',
        }
    }
    if (isYearPeriod(deviationValue)) {
        return {
            formattedValue: decimalToLocalizedString(deviationValue.value, false, 2, 0, locale),
            value: deviationValue.value,
            unit: Math.abs(deviationValue.value) === 1 ? yearUnitLabels.one : yearUnitLabels.other,
        }
    }

    if (typeof deviationValue === 'number') {
        return {
            formattedValue: decimalToLocalizedString(deviationValue, false, 2, 2, locale),
            value: deviationValue,
            unit: undefined,
        }
    }
    return undefined
}

type DeviationValueProps = {
    deviationValue?: DeviationValueType
    bold?: boolean
    locale?: string
    yearUnitLabels?: { one: string; other: string }
}

export function DeviationValue({ deviationValue, bold, locale, yearUnitLabels }: DeviationValueProps) {
    const valueAndUnit = getDeviationValueAndUnit(deviationValue, undefined, undefined, locale, yearUnitLabels)

    return (
        <Stack direction="row" gap={tokens.SizingSpacing075} alignItems="center">
            <Typography variant="textS" color={tokens.ColorTextBold} bold={bold}>
                {valueAndUnit ? valueAndUnit.formattedValue : '-'}
            </Typography>
            {valueAndUnit?.unit && (
                <Typography variant="textS" color={tokens.ColorTextSubtle} bold={bold}>
                    {valueAndUnit.unit}
                </Typography>
            )}
        </Stack>
    )
}
